import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { convertDisplayToDb } from "@/utils/currency";
import {
  formatInvalidInfluencerHandlesMessage,
  validateInfluencerHandles,
  type ValidatedInfluencerProfile,
} from "@/lib/influencerValidation";
import type {
  CampaignFormData,
  CampaignInsert,
  CampaignInfluencerInsert,
  CampaignRow,
  InfluencerInsert,
  InfluencerRow,
} from '@/components/campaign-creation/campaignFormTypes';
import {
  DEFAULT_DELIVERABLES,
  DEFAULT_INFLUENCER_STATUS,
  DEFAULT_CAMPAIGN_STATUS,
  DEFAULT_KANBAN_STAGE,
} from '@/components/campaign-creation/campaignFormTypes';
import type { Brand } from '@/hooks/useBrands';

interface CreateCampaignParams {
  formData: CampaignFormData;
  mergedHandles: string[];
  organizationId: string;
  isBrandOrg: boolean;
  brands: Brand[] | undefined;
  enqueueJob: (ids: string[], handles: string[]) => Promise<unknown>;
  userCountry: string | null;
}

/**
 * Looks up existing influencers by handle within an organization.
 * Creates any missing influencers and triggers enrichment for them.
 */
async function getInfluencersForHandles(
  handles: string[],
  organizationId: string,
  enqueueJob: (ids: string[], handles: string[]) => Promise<unknown>
): Promise<InfluencerRow[]> {
  if (handles.length === 0) {
    return [];
  }

  const { data: existingInfluencers, error: lookupError } = await supabase
    .from("influencers")
    .select("*")
    .eq("organization_id", organizationId)
    .in("handle", handles);

  if (lookupError) {
    throw new Error(`Failed to check existing influencers: ${lookupError.message}`);
  }

  const existing = existingInfluencers ?? [];
  const existingHandles = new Set(existing.map(influencer => influencer.handle.toLowerCase()));
  const missingHandles = handles.filter(handle => !existingHandles.has(handle));

  if (missingHandles.length === 0) {
    return existing;
  }

  const validation = await validateInfluencerHandles(missingHandles);
  const profileByHandle = new Map<string, ValidatedInfluencerProfile>(
    validation.profiles.map(profile => [profile.handle, profile]),
  );

  if (validation.invalidHandles.length > 0) {
    throw new Error(formatInvalidInfluencerHandlesMessage(validation.invalidHandles));
  }

  const influencerPayloads: InfluencerInsert[] = validation.validHandles.map(handle => {
    const profile = profileByHandle.get(handle);

    return {
      handle,
      name: profile?.name ?? handle,
      platform: 'instagram',
      organization_id: organizationId,
      enrichment_status: profile ? 'completed' : 'pending',
      // stamped when profile data was fetched at creation, so the next
      // refresh is charged by the profile-refresh metering trigger
      last_refreshed_at: profile ? new Date().toISOString() : null,
      followers_count: profile?.followersCount ?? null,
      following_count: profile?.followingCount ?? null,
      profile_pic_url: profile?.profilePicUrl ?? null,
      bio: profile?.bio ?? null,
      business_category: profile?.businessCategory ?? null,
      engagement_rate: profile?.engagementRate ?? null,
      engagement_rate_views: profile?.engagementRateViews ?? null,
      engagement_rate_followers: profile?.engagementRateFollowers ?? null,
      video_view_count: profile?.videoViewCount ?? null,
      verified: profile?.verified ?? null,
      instagram_pk: profile?.instagramPk ?? null,
    };
  });

  const { data: createdInfluencers, error: insertError } = await supabase
    .from("influencers")
    .insert(influencerPayloads)
    .select("*");

  if (insertError) {
    throw new Error(`Failed to create missing influencers: ${insertError.message}`);
  }

  return [...existing, ...((createdInfluencers ?? []) as InfluencerRow[])];
}

/**
 * Attaches a list of influencers to a campaign with default deliverables and status.
 */
async function attachInfluencersToCampaign(campaignId: string, influencers: InfluencerRow[]): Promise<void> {
  const uniqueInfluencerIds = Array.from(new Set(influencers.map(influencer => influencer.id)));

  if (uniqueInfluencerIds.length === 0) {
    return;
  }

  const mappingPayloads: CampaignInfluencerInsert[] = uniqueInfluencerIds.map(influencerId => ({
    campaign_id: campaignId,
    influencer_id: influencerId,
    status: DEFAULT_INFLUENCER_STATUS,
    deliverables: DEFAULT_DELIVERABLES,
  }));

  const { error } = await supabase
    .from("campaign_influencers")
    .insert(mappingPayloads);

  if (error) {
    throw new Error(`Failed to attach influencers to campaign: ${error.message}`);
  }
}

/**
 * Orchestrates the full campaign creation flow:
 * 1. Resolves influencer handles (creates missing ones)
 * 2. Inserts the campaign row
 * 3. Attaches influencers to the campaign
 *
 * @returns The created campaign row on success, or null on failure.
 */
export async function createCampaignWithInfluencers(
  params: CreateCampaignParams
): Promise<CampaignRow | null> {
  const { formData, mergedHandles, organizationId, isBrandOrg, brands, enqueueJob, userCountry } = params;

  // For brand-type orgs, brandId may still be '' if brands haven't loaded yet;
  // resolve it from the brands list at submit time, or fetch/create from the database.
  let resolvedBrandId = formData.brandId || (isBrandOrg ? (brands?.[0]?.id ?? null) : null);

  if (isBrandOrg && !resolvedBrandId) {
    const { data: existingBrand } = await supabase
      .from('brands')
      .select('id')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();

    if (existingBrand?.id) {
      resolvedBrandId = existingBrand.id;
    } else {
      const { data: orgData } = await supabase
        .from('organizations')
        .select('name')
        .eq('id', organizationId)
        .maybeSingle();

      const orgName = orgData?.name?.trim() || 'Brand';
      const { data: newBrand, error: brandInsertError } = await supabase
        .from('brands')
        .insert({
          organization_id: organizationId,
          name: orgName,
        })
        .select('id')
        .single();

      if (!brandInsertError && newBrand?.id) {
        resolvedBrandId = newBrand.id;
      }
    }
  }

  const nextMonth = new Date(formData.startDate);
  nextMonth.setMonth(nextMonth.getMonth() + 1);
  const fallbackEndDate = nextMonth.toISOString().split("T")[0];

  const { data: authData } = await supabase.auth.getUser();
  const currentUserId = authData?.user?.id ?? null;

  const payload: CampaignInsert = {
    name: formData.name,
    brand_id: resolvedBrandId,
    content_requirements: formData.hashtags || null,
    goal_type: formData.campaignType,
    platform_targeted: formData.platforms.length > 0 ? formData.platforms : null,
    total_budget: formData.totalBudget
      ? convertDisplayToDb(Number(formData.totalBudget), userCountry)
      : null,
    organization_id: organizationId,
    start_date: formData.startDate || null,
    end_date: formData.endDate || fallbackEndDate,
    status: DEFAULT_CAMPAIGN_STATUS,
    kanban_stage: DEFAULT_KANBAN_STAGE,
    created_by: currentUserId,
  };

  const influencers = await getInfluencersForHandles(mergedHandles, organizationId, enqueueJob);

  const { data, error } = await supabase.from("campaigns").insert([payload]).select().single();

  if (error) throw error;

  await attachInfluencersToCampaign(data.id, influencers);

  toast({
    title: "Campaign created successfully!",
    description: `Campaign "${data.name}" has been created with ${influencers.length} creator${influencers.length === 1 ? '' : 's'}.`,
  });

  return data;
}
